// Recalls (UC-APPOINTMENTS-013; INT-85, rule 34, BR-12, BR-APPOINTMENTS-017): the patients due back — the date a completed
// eye test or contact-lens fitting set, the source type, the last visit, RemindersRecalls consent by email and whether a
// recall-source appointment is already booked (A5) — for a branch, a type and a date range, with Snooze (a date and a reason)
// and Dismiss (a reason). The prototype names the page from Today's Needs attention ("Recalls") and the Reports tabs; it is
// built with the list conventions of patients.html (toolbar, dense table, keyset pager). Filters live in the URL. Sending the
// recall messages is Sprint 6 (messaging); nothing here sends.
import type { RecallItem } from "@visionx/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { appointmentTypes, recalls } from "../api/endpoints.appointments";
import { describeError, isApiError } from "../api/problem";
import { addDays, formatDate, isLocalDate, localDate } from "../lib/format";
import { can, useSessionView } from "../session/store";
import { WRITE_BLOCK_TITLE, useWriteBlock } from "../shell/OfflineBanner";
import { Alert, Avatar, Chip, Empty, ErrorAlert, Field, Loading, Modal, Pager, Panel, Select, TextArea, TextInput } from "../ui/bits";
import { initialsOfDisplay } from "./AppointmentsBits";
import { NoAccessPanel, PageHead } from "./common";

const PAGE = 50;
/** The API's cap on the range (RecallListQuerySchema). */
const MAX_RANGE_DAYS = 366;
/**
 * The default window: overdue by up to 30 days, or due in the next 30. Today's Needs attention counts the same window, so the
 * number on Today is the number of rows this page opens with.
 */
export const RECALL_WINDOW = { back: 30, ahead: 30 } as const;
export const recallWindow = (today: string): { from: string; to: string } => ({ from: addDays(today, -RECALL_WINDOW.back), to: addDays(today, RECALL_WINDOW.ahead) });

/** `10 Sept 2027` for a calendar date — the date itself, never shifted by a timezone. */
export const formatDay = (date: string): string =>
  new Intl.DateTimeFormat("en-ZA", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(new Date(`${date}T00:00:00.000Z`));
const daysBetween = (from: string, to: string): number => (Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`)) / 86_400_000;

/** BR-APPOINTMENTS-012: an Owner or Admin may read every branch at once (`branchId=all`); anyone else reads a branch they act in. */
const readsAllBranches = (role: string | undefined): boolean => role === "Owner" || role === "Admin";

export function Recalls() {
  const s = useSessionView();
  const qc = useQueryClient();
  const block = useWriteBlock();
  const online = block === null;
  const [params, setParams] = useSearchParams();
  const today = localDate(new Date());
  const defaults = recallWindow(today);
  const fromParam = params.get("from");
  const toParam = params.get("to");
  // A date in the address that is not on the calendar sends the page to its default window — both ends, so that a good end is
  // never paired with a default start it may precede (UC-APPOINTMENTS-013 A6; review, 4 Oct 2026).
  const impossible = (fromParam !== null && !isLocalDate(fromParam)) || (toParam !== null && !isLocalDate(toParam));
  const from = !impossible && fromParam !== null ? fromParam : defaults.from;
  const to = !impossible && toParam !== null ? toParam : defaults.to;
  const all = readsAllBranches(s?.user.role);
  const branchParam = params.get("branchId");
  const branchId = branchParam === "all" && all ? "all" : branchParam && s?.switchableBranches.some((b) => b.id === branchParam) ? branchParam : (s?.activeBranchId ?? undefined);
  const typeId = params.get("typeId") ?? undefined;
  const rangeError = daysBetween(from, to) < 0 ? "The end date is before the start date." : daysBetween(from, to) > MAX_RANGE_DAYS ? `Choose at most ${MAX_RANGE_DAYS} days.` : null;
  const [cursors, setCursors] = useState<string[]>([]);
  const cursor = cursors[cursors.length - 1];
  const list = useQuery({
    queryKey: ["recalls", branchId ?? "", typeId ?? "", from, to, cursor ?? ""],
    queryFn: () => recalls.list({ from, to, branchId, typeId, cursor, limit: PAGE, count: true }),
    enabled: !!branchId && rangeError === null,
  });
  const types = useQuery({ queryKey: ["appointment-types"], queryFn: () => appointmentTypes.list() });
  const typeRows = types.data?.data.items ?? [];
  const typeName = (id: string | null, code: string | null): string | null => (id ? typeRows.find((t) => t.id === id)?.name : undefined) ?? code;
  const [notice, setNotice] = useState<ReactNode | null>(null);
  const [modal, setModal] = useState<{ kind: "snooze" | "dismiss"; item: RecallItem } | null>(null);
  const canWrite = can(s, "appointments.recall.write");
  const setParam = (patch: Record<string, string | null>) => {
    setCursors([]);
    // While the address holds an impossible date the page shows its default window: an edit of either end writes the other as
    // shown, so the impossible one leaves the address instead of sending the edit back to the default (review, 4 Oct 2026).
    const shown = impossible && ("from" in patch || "to" in patch) ? { from, to } : {};
    const next = { ...shown, ...patch };
    setParams((p) => { const n = new URLSearchParams(p); for (const [k, v] of Object.entries(next)) { if (v === null || v === "") n.delete(k); else n.set(k, v); } return n; }, { replace: true });
  };
  const filtered = [...params.keys()].some((k) => ["from", "to", "branchId", "typeId"].includes(k));
  const rows = list.data?.data.items ?? [];
  const total = list.data?.data.totalCount;
  const first = cursors.length * PAGE + 1;
  const branchLabel = branchId === "all" ? "All branches" : s?.switchableBranches.find((b) => b.id === branchId)?.name ?? s?.assignments.find((a) => a.branchId === branchId)?.branchName ?? "";
  const denied = list.isError && isApiError(list.error) && list.error.code === "PERMISSION_DENIED";
  const done = (n: ReactNode) => {
    setModal(null);
    setNotice(n);
    void qc.invalidateQueries({ queryKey: ["recalls"] });
    void qc.invalidateQueries({ queryKey: ["attention"] });
  };

  return (
    <section className="screen" id="s-recalls">
      <div className="content">
        <PageHead
          title="Recalls"
          sub={`${branchLabel} · due ${formatDay(from)} – ${formatDay(to)}${total !== undefined ? ` · ${total} due` : ""}`}
          actions={<Link className="btn" to="/appointments">Open calendar</Link>}
        />
        <div className="toolbar">
          {s && (s.canSwitchBranch || all) ? (
            <Select aria-label="Branch" value={branchId ?? ""} onChange={(e) => setParam({ branchId: e.target.value === s.activeBranchId ? null : e.target.value })}>
              {s.switchableBranches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              {all && <option value="all">All branches</option>}
            </Select>
          ) : <div className="input ro">{branchLabel}</div>}
          <Select aria-label="Type" value={typeId ?? ""} onChange={(e) => setParam({ typeId: e.target.value || null })}>
            <option value="">Type · Any</option>
            {typeRows.filter((t) => t.isRecallSource).map((t) => <option key={t.id} value={t.id}>Type · {t.name}{t.isActive ? "" : " (inactive)"}</option>)}
          </Select>
          <span className="small muted">Due</span>
          <DateFilter label="Due from" value={from} max={to} error={rangeError !== null} onCommit={(v) => setParam({ from: v })} />
          <span className="small muted">to</span>
          <DateFilter label="Due to" value={to} min={from} error={rangeError !== null} onCommit={(v) => setParam({ to: v })} />
          {filtered && <button className="btn sm" type="button" onClick={() => { setCursors([]); setParams(new URLSearchParams(), { replace: true }); }}>Reset</button>}
          <span className="right small faint">Opens on the last {RECALL_WINDOW.back} days and the next {RECALL_WINDOW.ahead}</span>
        </div>
        {rangeError && <Alert tone="warn"><b>{rangeError}</b> The list shows recalls due between the two dates.</Alert>}
        {notice}
        {list.isPending && list.fetchStatus !== "idle" && <Loading what="recalls" />}
        {list.isError && (denied ? <NoAccessPanel page="Recalls" error={list.error} /> : <ErrorAlert error={list.error} />)}
        {list.data && (
          <Panel title="Due recalls" meta={rows.length === 0 ? "" : `${first}–${first + rows.length - 1}${total !== undefined ? ` of ${total}` : ""} · earliest due first`} tight
            foot={<>
              <span className="small faint">Recall messages are sent from Sprint 6 · Dormant, Prohibited and Archived patients are not listed</span>
              <Pager label={`Page ${cursors.length + 1}`} hasPrev={cursors.length > 0} hasNext={!!list.data.data.nextCursor} onPrev={() => setCursors((c) => c.slice(0, -1))} onNext={() => setCursors((c) => [...c, list.data!.data.nextCursor!])} />
            </>}>
            {rows.length === 0 ? (
              <Empty title={`No recalls due between ${formatDay(from)} and ${formatDay(to)}.`}>A recall date is set when an eye test or contact-lens fitting is completed. Widen the dates or choose another branch or type.</Empty>
            ) : (
              <div className="tbl-scroll">
                <table>
                  <thead><tr><th>Due</th><th>Patient</th><th>Type</th><th>Last visit</th><th>Reminders</th><th>Booked</th><th></th></tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.patient.id}>
                        <td className="nw">{formatDay(r.dueDate)}{r.dueDate < today ? <> <Chip tone="warn">Overdue</Chip></> : r.dueDate === today ? <> <Chip tone="info">Today</Chip></> : null}</td>
                        <td><div className="name-cell"><Avatar initials={initialsOfDisplay(r.patient.displayName)} size="sm" /><div><Link to={`/patients/${r.patient.id}`}>{r.patient.displayName}</Link><div className="sub mono">{r.patient.accountCode}{r.patient.isDependant ? ` · dependant, ${r.patient.age}` : ""}</div></div></div></td>
                        <td>{typeName(r.typeId, r.typeCode) ?? <span className="faint">set without a visit</span>}</td>
                        <td className="nw">{r.lastCompletedAt ? formatDate(r.lastCompletedAt) : <span className="faint">—</span>}</td>
                        <td>{r.consent.email ? <Chip tone="ok" dot>Email</Chip> : <span className="faint">No consent</span>}</td>
                        <td>{r.alreadyBooked ? <Chip tone="info" dot>Booked</Chip> : <span className="faint">Not yet</span>}</td>
                        <td className="r nw">{canWrite && <span className="row c gap-4">
                          <button className="btn sm" type="button" disabled={!online} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => setModal({ kind: "snooze", item: r })}>Snooze…</button>
                          <button className="btn sm ghost" type="button" disabled={!online} title={block ? WRITE_BLOCK_TITLE[block] : undefined} onClick={() => setModal({ kind: "dismiss", item: r })}>Dismiss…</button>
                        </span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        )}
        {!branchId && <Alert tone="warn"><b>No active branch.</b> Choose a branch in the top bar to see its recalls.</Alert>}
      </div>
      {modal?.kind === "snooze" && <SnoozeModal item={modal.item} today={today} onClose={() => setModal(null)} onDone={(until) => done(<Alert tone="ok"><b>{modal.item.patient.displayName}'s recall snoozed to {formatDay(until)}.</b> The reason is recorded in the patient's history.</Alert>)} onGone={() => { void qc.invalidateQueries({ queryKey: ["recalls"] }); }} />}
      {modal?.kind === "dismiss" && <DismissModal item={modal.item} onClose={() => setModal(null)} onDone={() => done(<Alert tone="ok"><b>{modal.item.patient.displayName}'s recall dismissed.</b> The next completed eye test or contact-lens fitting sets a new one.</Alert>)} onGone={() => { void qc.invalidateQueries({ queryKey: ["recalls"] }); }} />}
    </section>
  );
}

/** A refusal the modal explains in words: the recall went meanwhile (E2), or anything else. */
function RecallError({ error, name }: { error: unknown; name: string }) {
  if (error === null) return null;
  if (isApiError(error) && error.code === "ILLEGAL_TRANSITION") return <Alert tone="warn"><b>{name} has no recall due any more.</b> It was dismissed or moved meanwhile; the list is refreshed.</Alert>;
  if (isApiError(error) && error.code === "VALIDATION_FAILED" && error.errors.every((e) => e.path === "until" || e.path === "reason")) return null;
  return <Alert tone="crit"><b>Could not change the recall.</b> {describeError(error)}</Alert>;
}

function SnoozeModal({ item, today, onClose, onDone, onGone }: { item: RecallItem; today: string; onClose: () => void; onDone: (until: string) => void; onGone: () => void }) {
  const [until, setUntil] = useState(() => addDays(today, 14));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const fieldErr = (path: string) => (isApiError(error) ? error.field(path)?.message : undefined);
  const past = isLocalDate(until) && until < today;
  const ready = isLocalDate(until) && !past && reason.trim().length > 0 && !busy;
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await recalls.snooze(item.patient.id, { until, reason: reason.trim() });
      onDone(data.dueDate ?? until);
    } catch (err) {
      setError(err);
      if (isApiError(err) && err.code === "ILLEGAL_TRANSITION") onGone();
    } finally {
      setBusy(false);
    }
  };
  const quick = (label: string, date: string) => <button className="btn sm ghost" type="button" onClick={() => setUntil(date)}>{label}</button>;
  return (
    <Modal title={`Snooze ${item.patient.displayName}'s recall`} meta={`due ${formatDay(item.dueDate)}`} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Cancel</button>
      <button className="btn primary" type="submit" disabled={!ready}>{busy ? "Snoozing…" : "Snooze recall"}</button>
    </>}>
      <RecallError error={error} name={item.patient.displayName} />
      <Field label="Until" required htmlFor="snooze-until" error={past ? "Choose today or a later date." : fieldErr("until")} help="The recall is due again on this date.">
        <TextInput id="snooze-until" type="date" min={today} value={until} onChange={(e) => setUntil(e.target.value)} mono error={past || !!fieldErr("until")} autoFocus />
      </Field>
      <div className="row c gap-4">{quick("In 2 weeks", addDays(today, 14))}{quick("In 1 month", addDays(today, 30))}{quick("In 3 months", addDays(today, 91))}</div>
      <Field label="Reason" required htmlFor="snooze-reason" error={fieldErr("reason")} help="Recorded with the change in the patient's history.">
        <TextArea id="snooze-reason" value={reason} maxLength={500} rows={2} onChange={(e) => setReason(e.target.value)} placeholder="Away — will book on return" />
      </Field>
    </Modal>
  );
}

function DismissModal({ item, onClose, onDone, onGone }: { item: RecallItem; onClose: () => void; onDone: () => void; onGone: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const fieldErr = (path: string) => (isApiError(error) ? error.field(path)?.message : undefined);
  const ready = reason.trim().length > 0 && !busy;
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await recalls.dismiss(item.patient.id, { reason: reason.trim() });
      onDone();
    } catch (err) {
      setError(err);
      if (isApiError(err) && err.code === "ILLEGAL_TRANSITION") onGone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Dismiss ${item.patient.displayName}'s recall`} meta={`due ${formatDay(item.dueDate)}`} onClose={onClose} onSubmit={() => void submit()} footer={<>
      <button className="btn" type="button" onClick={onClose}>Keep it</button>
      <button className="btn danger" type="submit" disabled={!ready}>{busy ? "Dismissing…" : "Dismiss recall"}</button>
    </>}>
      <RecallError error={error} name={item.patient.displayName} />
      <p className="muted">The due date is cleared; the next completed eye test or contact-lens fitting sets a new one.</p>
      <Field label="Reason" required htmlFor="dismiss-reason" error={fieldErr("reason")} help="Recorded with the change in the patient's history.">
        <TextArea id="dismiss-reason" value={reason} maxLength={500} rows={2} autoFocus onChange={(e) => setReason(e.target.value)} placeholder="Moved to Cape Town" />
      </Field>
    </Modal>
  );
}

/**
 * A date filter, held here while it is typed: the address gets it once it is a whole calendar date from 1900 on, and an emptied
 * field on leaving it. Bound to the address directly, each keystroke waited on the router's transition and the field snapped
 * back, so a year could not be typed; and a year half-typed (0002) read as an impossible date and sent the page to its default
 * window, losing the other end (review, 5 Oct 2026). Leaving a half-typed date puts back the one applied.
 */
function DateFilter({ label, value, min, max, error, onCommit }: { label: string; value: string; min?: string; max?: string; error: boolean; onCommit: (v: string | null) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const whole = (v: string) => isLocalDate(v) && v >= "1900-01-01";
  return (
    <TextInput type="date" aria-label={label} value={draft} min={min} max={max} mono error={error}
      onChange={(e) => { const v = e.target.value; setDraft(v); if (whole(v) && v !== value) onCommit(v); }}
      // Chrome reports a half-typed date as "" too, telling it apart only by `badInput`: that one puts back the date applied. One
      // emptied returns its end to the default, and shows it — the address may already hold the default, so nothing else re-reads it.
      onBlur={(e) => { if (draft === "" && !e.currentTarget.validity?.badInput) { onCommit(null); setDraft(value); } else if (!whole(draft)) setDraft(value); }} />
  );
}
